import {
  Box,
  ButtonBase,
  Chip,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import moment from "moment";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { convertPrice } from "utils/currency.util";
import useQuery from "utils/router.util";
import PayAmount from "./pay-amount";

const ListItem = ({
  data,
  bookingId = "",
}: {
  data: any;
  bookingId?: string;
}) => {
  const [paynow, showPaynow] = useState(false);
  const navigate = useNavigate();
  const query = useQuery();
  const firstItem = data?.booking_items?.[0]?.listings;
  const totalItems = data?.booking_items?.length;
  const bookingPaymentSummary = data?.booking_payments?.[0];
  const type: any = query.get("type") || "requested";

  const theme = useTheme();
  const matchesSm = useMediaQuery(theme.breakpoints.down("sm"));
  const matchesXs = useMediaQuery(theme.breakpoints.only("xs"));

  const statusLabel = () => {
    if (type === "requested" && data.booking_status !== 2)
      return "Status: Pending";
    if (type === "requested" && data.booking_status === 2)
      return "Status: Payment Pending";
    if (type === "confirmed") return "Status: Confirmed";
    if (type === "completed")
      return data.booking_status === 7
        ? "Status: Returned"
        : "Status: Delivered";
    if (type === "cancelled") return "Status: Cancelled";
    return "Status: Unknown";
  };

  const statusSxProps = () => {
    if (type === "requested" && data.booking_status !== 2)
      return {
        background: "#FEF6E1",
        color: "#F1B724",
        fontSize: "13px",
        fontWeight: 500,
        lineHeight: "16px",
        width: "144px",
        height: "32px",
      };
    if (type === "requested" && data.booking_status === 2)
      return {
        background: "#FF3A441F",
        color: "#D21A00",
        fontSize: "13px",
        fontWeight: 500,
        lineHeight: "16px",
        width: "180px",
        height: "32px",
      };
    if (type === "confirmed")
      return {
        background: "#EFFFED",
        color: "#4EA643",
        fontSize: "13px",
        fontWeight: 500,
        lineHeight: "16px",
        width: "144px",
        height: "32px",
      };
    if (type === "completed")
      return {
        background: "#F4F4F4",
        color: "#323232",
        fontSize: "13px",
        fontWeight: 500,
        lineHeight: "16px",
        width: "144px",
        height: "32px",
      };
    if (type === "cancelled")
      return {
        background: "#FF3A441F",
        color: "#D21A00",
        fontSize: "13px",
        fontWeight: 500,
        lineHeight: "16px",
        width: "144px",
        height: "32px",
      };
  };
  return (
    <Box
      sx={{
        display: "flex",
        gap: { md: "20px", lg: "20px", xl: "20px", sm: "10px", xs: "10px" },
        flexWrap: "wrap",
        justifyContent: "space-between",
      }}
    >
      <Box
        component="img"
        src={firstItem?.images?.[0]?.preview_url}
        alt=""
        sx={{
          height: {
            sm: "96px",
            xs: "96px",
            lg: "88px",
            xl: "88px",
            md: "88px",
          },
          width: {
            sm: "96px",
            xs: "96px",
            lg: "128px",
            xl: "128px",
            md: "128px",
          },
          borderRadius: "8px",
        }}
        onClick={() => navigate(`/booking-history/${bookingId}`)}
      />
      <Box
        sx={{
          flex: 1,
          display: "flex",
          justifyContent: "space-between",
          flexWrap: {
            md: "nowrap",
            lg: "nowrap",
            xl: "nowrap",
            sm: "wrap",
            xs: "wrap",
          },
          gap: "10px",
          borderBottom: "1px solid #E2E2E2",
          pb: "10px",
        }}
      >
        <Box onClick={() => navigate(`/booking-history/${bookingId}`)}>
          <Typography
            sx={{
              fontSize: {
                md: "20px",
                lg: "20px",
                xl: "20px",
                sm: "14px",
                xs: "14px",
              },
              lineHeight: {
                md: "31px",
                lg: "31px",
                xl: "31px",
                sm: "20px",
                xs: "20px",
              },
              fontWeight: "600",
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: matchesSm || matchesXs ? 2 : 1,
              WebkitBoxOrient: "vertical",
            }}
          >
            {firstItem?.listing_name}
            &nbsp;&nbsp;
            {totalItems - 1 > 0 && (
              <Typography
                component="span"
                sx={{
                  color: "#307AFF",
                  fontSize: {
                    md: "18px",
                    lg: "18px",
                    xl: "18px",
                    sm: "14px",
                    xs: "14px",
                  },
                  lineHeight: {
                    md: "31px",
                    lg: "31px",
                    xl: "31px",
                    sm: "20px",
                    xs: "20px",
                  },
                  fontWeight: 600,
                  textDecoration: "underline",
                }}
              >
                +{totalItems - 1} more
              </Typography>
            )}
          </Typography>
          <Typography
            sx={{
              color: "#6E8C20",
              fontWeight: 600,
              fontSize: {
                md: "18px",
                lg: "18px",
                xl: "18px",
                sm: "12px",
                xs: "12px",
              },
              lineHeight: {
                md: "31px",
                lg: "31px",
                xl: "31px",
                sm: "20px",
                xs: "20px",
              },
            }}
          >
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
              currencyDisplay: "narrowSymbol",
            }).format(bookingPaymentSummary?.total)}{" "}
            • {data?.delivery_type === 0 ? "Pickup" : "Delivery"}
            &nbsp;
            {data?.amount_due > 0 && (
              <Typography
                component="span"
                sx={{
                  color: "#D21A00",
                  fontWeight: 500,
                  fontSize: {
                    md: "18px",
                    lg: "18px",
                    xl: "18px",
                    sm: "12px",
                    xs: "12px",
                  },
                  lineHeight: {
                    md: "31px",
                    lg: "31px",
                    xl: "31px",
                    sm: "20px",
                    xs: "20px",
                  },
                  textWrap: "nowrap",
                }}
              >
                (Pending Amt: {convertPrice(data?.amount_due)})
              </Typography>
            )}
          </Typography>
          <Typography
            sx={{
              color: "#A2A2A2",
              fontSize: {
                md: "16px",
                lg: "16px",
                xl: "16px",
                sm: "12px",
                xs: "12px",
              },
              fontWeight: 500,
              lineHeight: {
                md: "31px",
                lg: "31px",
                xl: "31px",
                sm: "20px",
                xs: "20px",
              },
            }}
          >
            {moment(
              data?.updated_at ? data?.updated_at : data?.created_at
            ).fromNow()}
          </Typography>
        </Box>

        <Box
          sx={{
            display: {
              md: "flex",
              lg: "flex",
              xl: "flex",
              sm: "none",
              xs: "none",
            },
            flexDirection: {
              md: "column",
              lg: "column",
              xl: "column",
              sm: "row",
              xs: "row",
            },
            justifyContent: {
              md: "center",
              lg: "center",
              xl: "center",
              sm: "space-between",
              xs: "space-between",
            },
            gap: "10px",
            flex: {
              sm: 1,
              xs: 1,
            },
            alignItems: {
              sm: "center",
              xs: "center",
              md: "flex-end",
              lg: "flex-end",
              xl: "flex-end",
            },
          }}
        >
          <Chip label={statusLabel()} sx={statusSxProps()} />
          {data.booking_status !== 2 && (
            <ButtonBase
              sx={{
                height: "40px",
                width: "144px",

                background: "#6E8C20",
                borderRadius: "10px",
              }}
            >
              <Typography
                sx={{ fontWeight: 500, fontSize: "15px", color: "#FFF" }}
                onClick={() => navigate(`/booking-history/${bookingId}`)}
              >
                Check Details
              </Typography>
            </ButtonBase>
          )}
          {data.booking_status === 2 && (
            <Stack gap={1} flexDirection="row">
              <ButtonBase
                sx={{
                  height: "40px",
                  width: "144px",

                  background: "#e4e4e4",
                  borderRadius: "10px",
                }}
              >
                <Typography
                  sx={{ fontWeight: 500, fontSize: "15px", color: "#000000" }}
                  onClick={() => navigate(`/booking-history/${bookingId}`)}
                >
                  Check Details
                </Typography>
              </ButtonBase>
              <ButtonBase
                sx={{
                  height: "40px",
                  width: "144px",

                  background: "#6E8C20",
                  borderRadius: "10px",
                }}
                onClick={() => showPaynow(true)}
              >
                <Typography
                  sx={{ fontWeight: 500, fontSize: "15px", color: "#FFF" }}
                >
                  Pay Now
                </Typography>
              </ButtonBase>
            </Stack>
          )}
        </Box>

        {data.booking_status === 2 && (
          <ButtonBase
            sx={{
              height: "40px",
              width: "144px",
              display: {
                md: "none",
                lg: "none",
                xl: "none",
                sm: "flex",
                xs: "flex",
              },
              background: "#6E8C20",
              borderRadius: "10px",
            }}
            onClick={() => showPaynow(true)}
          >
            <Typography
              sx={{ fontWeight: 500, fontSize: "15px", color: "#FFF" }}
            >
              Pay Now
            </Typography>
          </ButtonBase>
        )}
      </Box>

      {paynow && (
        <PayAmount
          onClose={() => showPaynow(false)}
          data={data}
          bookingId={bookingId}
        />
      )}
    </Box>
  );
};

export default ListItem;
